<template>
  <section class="c-px-1 c-pb-5 c-xl:c-pb-0">
    <div class="container">
      <div class="row c-items-center c-justify-center relative c-xl:block clearfix">
        <!-- bubble top -->
        <div class="pattern-2 pattern-2__intro embed-responsive embed-responsive-1by1 bg-light opacity-40 col-3 hidden c-md:block absolute" />
        <!-- bubble bottom -->
        <div class="pattern-1 pattern-1__intro embed-responsive embed-responsive-1by1 bg-light opacity-40 col-2 hidden c-md:block absolute" />
        <!-- image (gallery) -->
        <div class="c-sm:col-9 c-md:col-5 c-xl:col-6 c-xl:c-pl-2 c-xl:c-pr-5 order-1 c-md:order-0 c-float-left">
          <div class="hero-carousel">
            <div class="row overflow-hidden c-pb-7 c-lg:c-py-5 c-lg:mx-n6">
              <div
                class="row c-mx-0 w-100 c-mb-0 c-flex-nowrap c-flex-grow-1 h-auto list-unstyled"
              >
                <div class="col-12 c-lg:c-px-6">
                  <div class="hero-carousel__image c-mb-0">
                    <div
                      class="hero-carousel__image__in no-padding"
                    >
                      <RemoteResponsiveImage
                        :product-image-data="article.image"
                        img-class="hero-carousel__image__in-img"
                        :alt="article.title"
                        sizes="(min-width: 1200px) 620px, (min-width: 500px) 582px, 70vw"
                        :lazy-load="false"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Heading -->
        <div class="c-md:col-7 c-xl:col-6 c-xl:c-px-5 c-mb-4 c-md:c-mb-0 order-0 c-md:order-0 c-float-right clear-right">
          <BreadCrumb :breadcrumb-list="breadcrumbList" />

          <div class="text-muted flex text-sm-c c-mb-2">
            <strong
              v-for="(item, index) in article.tags"
              :key="index"
            >
              {{ item.name }}
              <span
                v-if="index < article.tags.length - 1"
                class="c-mx-1"
              >•</span>
            </strong>
          </div>
          <h1 class="heading-4 c-mb-4 c-md:c-mb-5">
            {{ article.title }}
          </h1>
          <BlogShare />
        </div>
        <!-- Short description -->
        <div class="c-xl:col-6 c-xl:c-px-5 order-2 c-md:order-0 c-float-right clear-right">
          <div class="text">
            <p class="c-mt-4">
              {{ article.introText }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import BreadCrumb from '@/components/general/BaseBreadcrumb.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import BlogShare from '@/components/blog/BlogShare.vue'
import useHead from '@/mixins/headManager'
import type { Article } from '@/types/articles'

const props = defineProps<{
  article: Article
  permalink: string
}>()

const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const jsonld = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  'headline': props.article.title,
  'image': props.article.image.llg.formats["image/jpeg"] ?? props.article.image.lg.formats["image/jpeg"],
  'author': {
    '@type': 'Person',
    'name': import.meta.env.VITE_VUE_APP_SITE_NAME,
    'url': baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl,
  },
}
useHead({
  title: props.article.title,
  meta: [
    {
      property: `og:title`,
      content: computed(() => props.article.title),
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(jsonld),
    },
  ],
})

const breadcrumbList = ref([
  {
    name: 'Blog',
    link: { name: 'blogPath' },
  },
  {
    name: props.article.title,
    link: { name: 'showBlogPath', params: { permalink: props.permalink } },
  },
])
</script>
